import {
  addHours,
  isValid,
  lastDayOfMonth,
  startOfDay,
  subDays,
} from 'date-fns';
import { DateTime } from 'luxon';
import moment from 'moment';

const lastSundaysCache: { [key: string]: Date } = {};

export function formatDate(date: Date | null | undefined, strFormat?: string) {
  if (!date) {
    return null;
  }
  const dt = DateTime.fromJSDate(date).setLocale('fr');

  return dt.toFormat(strFormat || APP_DATE_FORMAT);

  /*/
  // Get timezone
  const tz = date.getTimezoneOffset();

  // The date still has the timezone but we manipulate it as an UTC Date
  date.setMinutes(date.getMinutes() + tz);

  const marchLastSunday = getLastSunday(date.getFullYear(), MONTHS.MARCH);
  const octoberLastSunday = getLastSunday(date.getFullYear(), MONTHS.OCTOBER);

  // Inclusive March Saturday / Exclusive October Last Saturday
  const isDayLightSaving = isWithinRange(
    date,
    marchLastSunday,
    subMilliseconds(octoberLastSunday, 1),
  );

  // Add our calculated timezone offset with DayLightSaving
  date.setHours(date.getHours() + (isDayLightSaving ? 2 : 1));

  return format(date, strFormat || APP_DATE_FORMAT, { locale: frLocale });
  /*/
}

function getLastSunday(year: number, month: number) {
  const key = `${year}${month}`;
  const cachedDate = lastSundaysCache[key];
  if (cachedDate) {
    return cachedDate;
  }

  let date = lastDayOfMonth(new Date(year, month));
  if (date.getDay() !== DAYS.SUNDAY) {
    date = subDays(date, date.getDay());
  }

  const res = addHours(startOfDay(date), 1);

  lastSundaysCache[key] = res;
  return res;
}

export function formatDateTime(date: Date | undefined) {
  return formatDate(date, APP_DATETIME_FORMAT);
}

export const dateFromISOString = (date: string): Date | null => {
  if (!date) {
    return null;
  }

  if (date.indexOf('Z') === -1) {
    return new Date(date + 'Z');
  }

  return new Date(date);
};

export function getDateUtc(dateString?: string): Date {
  if (!dateString) {
    if (arguments.length === 0) {
      return new Date();
    } else {
      return new Date();
    }
  }

  // temp fix while waiting for utc date-fns
  const [day, month, year] = dateString.substr(0, 10).split('/');
  if (day && month && year) {
    return new Date(`${year}-${month}-${day}`);
  } else {
    return new Date();
  }
}

export function getNextYearFirstJanuary(date: Date) {
  if (!date) {
    return null;
  }

  const utcDate = getDateUtc();
  if (!utcDate) {
    return null;
  }
  return new Date(utcDate.getFullYear() + 1, 0, 1);
}

export function isDateValid(date: string | Date | null | undefined): boolean {
  // cannot be null or undefined
  if (!date) {
    return false;
  }

  // if value not null and string, convert it into Date
  if (typeof date === 'string') {
    const convertedDate = getDateUtc(date);
    // date is null after convert
    if (!convertedDate) {
      return false;
    }
    date = convertedDate;
  }

  return isValid(date);
}

export function toDateOnly(date: Date) {
  return moment(date).format('LL');
}

export function daysElapsedSince(date: Date): number {
  const dateNow = new Date();
  dateNow.setHours(0, 0, 0, 0);
  const diff = Math.abs(dateNow.getTime() - date.getTime());
  const diffDays = Math.floor(diff / (1000 * 3600 * 24));

  return diffDays;
}

export function getAgeFromDate(date: any): number {
  const convertAge = new Date(date);
  const timeDiff = Math.abs(Date.now() - convertAge.getTime());
  let showAge: number = Math.floor(timeDiff / (1000 * 3600 * 24) / 365);

  return showAge;
}

export const APP_DATE_FORMAT = 'dd/MM/yyyy';
export const APP_DATETIME_FORMAT = 'dd/MM/yyyy à hh:mm';

export const DAYS = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 0,
};

export const MONTHS = {
  JANUARY: 0,
  FEBRUARY: 1,
  MARCH: 2,
  APRIL: 3,
  MAY: 4,
  JUNE: 5,
  JULY: 6,
  AUGUST: 7,
  SEPTEMBER: 8,
  OCTOBER: 9,
  NOVEMBER: 10,
  DECEMBER: 11,
};

// Calcul de l'âge en tenant compte des années bissextiles
export function getAgeFromDate2(date: string | Date): number {
  let birthDate!: Date;
  // Convertir la date en objet Date si c'est une chaîne
  if (typeof date === 'string') {
    const [day, month, year] = date.split('/');
    const dateString = `${year}/${month}/${day}`;
    birthDate = new Date(dateString);
  } else {
    birthDate = date;
  }

  // Vérifier si la date est valide
  if (isNaN(birthDate.getTime())) {
    throw new Error('Date de naissance invalide');
  }

  const today = new Date();

  // Calcul de l'âge en tenant compte des années bissextiles
  let age = today.getFullYear() - birthDate.getFullYear();

  // Vérifier si l'anniversaire est passé cette année
  const hasBirthdayPassed =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());

  // Si l'anniversaire n'est pas encore passé cette année, retirer 1 an
  if (!hasBirthdayPassed) {
    age--;
  }

  return age;
}
